import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, 
    Grid, 
    Paper, 
    Typography, 
    Skeleton,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    InputAdornment,
    TextField,
    Button
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useSelector } from 'react-redux';
import { FinanceService } from '../../../api/services/financeService';
import FinancialTrendsChart from './FinancialTrendsChart';
import { formatCurrency, useCurrency } from '../../../utils/currency';
import {
    standardFilterInputSx,
    standardResetButtonSx,
    standardRefreshButtonSx,
    standardSearchInputSx
} from '../../../components/common/tableStyles';

const getThirtyDaysAgo = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
};

const getToday = () => {
    return new Date().toISOString().split('T')[0];
};

export default function FinanceDashboardTab() {
    const { user, tenants } = useSelector((state) => state.auth || {});
    const { currency } = useCurrency();
    const [startDate, setStartDate] = useState(getThirtyDaysAgo());
    const [endDate, setEndDate] = useState(getToday());
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchDashboard = async (sDate = startDate, eDate = endDate) => {
        setLoading(true);
        try {
            const params = {};
            if (sDate) params.startDate = sDate;
            if (eDate) params.endDate = eDate;
            const data = await FinanceService.getFinanceDashboard(params);
            const responseData = data?.data || data || {};
            setDashboardData(responseData);
        } catch (error) {
            console.error('Failed to fetch finance dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResetRange = () => {
        const defStart = getThirtyDaysAgo();
        const defEnd = getToday();
        setStartDate(defStart);
        setEndDate(defEnd);
        fetchDashboard(defStart, defEnd);
    };

    useEffect(() => {
        fetchDashboard(startDate, endDate);
    }, [startDate, endDate, user?.tenantId, tenants]);

    // Financial Metrics extracted directly from backend response
    const monthRevenue = Number(dashboardData?.thisMonth?.revenue || 0);
    const monthExpenses = Number(dashboardData?.thisMonth?.expenses || 0);
    const monthProfit = Number(dashboardData?.thisMonth?.profit ?? (monthRevenue - monthExpenses));
    const profitMargin = monthRevenue > 0 ? ((monthProfit / monthRevenue) * 100).toFixed(1) : '0';


    const outstandingDues = useMemo(() => {
        return Array.isArray(dashboardData?.outstandingDues) ? dashboardData.outstandingDues : [];
    }, [dashboardData]);

    const totalOutstandingDues = useMemo(() => {
        return outstandingDues.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    }, [outstandingDues]);

    const filteredDues = useMemo(() => {
        if (!searchQuery.trim()) return outstandingDues;
        const q = searchQuery.toLowerCase();
        return outstandingDues.filter(item => 
            (item.folioId && item.folioId.toLowerCase().includes(q)) ||
            (item.reservationId && item.reservationId.toLowerCase().includes(q)) ||
            (item.guestId && item.guestId.toLowerCase().includes(q)) ||
            (item.amount && String(item.amount).includes(q))
        );
    }, [outstandingDues, searchQuery]);

    const renderMetricCard = (title, value, icon, loadingState, color = 'primary.main', subtitle = '') => (
        <Paper 
            sx={{ 
                p: 2.5, 
                borderRadius: '12px', 
                border: '1px solid #eaecf0', 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                bgcolor: '#ffffff',
                boxShadow: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: '#cbd5e1',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    transform: 'translateY(-1px)'
                }
            }} 
            elevation={0}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, fontSize: '13px' }}>
                    {title}
                </Typography>
                <Box 
                    sx={{ 
                        p: 1.25, 
                        borderRadius: '10px', 
                        bgcolor: `${color}14`, 
                        color: color, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {icon}
                </Box>
            </Box>
            {loadingState ? (
                <Skeleton variant="text" width="60%" height={45} />
            ) : (
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '24px', sm: '28px' }, letterSpacing: '-0.02em' }}>
                    {formatCurrency(value, currency)}
                </Typography>
            )}
            {subtitle && !loadingState && (
                <Typography variant="caption" sx={{ mt: 1.25, color: '#64748b', fontWeight: 600, fontSize: '11.5px', display: 'block' }}>
                    {subtitle}
                </Typography>
            )}
            {loadingState && subtitle && <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />}
        </Paper>
    );

    return (
        <Box>
            {/* Header Toolbar with Date Range Filter Controls */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3.5, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
                        Financial Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                        Consolidated monthly performance and outstanding receivables tracking.
                    </Typography>
                </Box>

                {/* Date Range Filter Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <TextField
                        size="small"
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...standardFilterInputSx, width: 160 }}
                    />
                    <TextField
                        size="small"
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: startDate }}
                        sx={{ ...standardFilterInputSx, width: 160 }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleResetRange}
                        startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                        sx={standardResetButtonSx}
                    >
                        Reset Range
                    </Button>
                    <Tooltip title="Refresh Dashboard">
                        <IconButton 
                            onClick={() => fetchDashboard(startDate, endDate)}
                            disabled={loading}
                            sx={standardRefreshButtonSx}
                        >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Dashboard Analytics Grid */}
            <Grid container spacing={3} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard(
                        "Revenue", 
                        monthRevenue, 
                        <AttachMoneyIcon sx={{ fontSize: 22 }} />, 
                        loading, 
                        '#16a34a'
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard(
                        "Expenses", 
                        monthExpenses, 
                        <TrendingDownIcon sx={{ fontSize: 22 }} />, 
                        loading, 
                        '#dc2626'
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard(
                        "Profit", 
                        monthProfit, 
                        <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />, 
                        loading, 
                        '#2563eb',
                        `Net Margin: ${profitMargin}%`
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard(
                        "Outstanding Dues", 
                        totalOutstandingDues, 
                        <PriceCheckIcon sx={{ fontSize: 22 }} />, 
                        loading, 
                        '#ea580c',
                        `${outstandingDues.length} Unpaid Folios`
                    )}
                </Grid>
            </Grid>


            {/* Financial Trends & Outstanding Receivables Section */}
            <Grid container spacing={3}>
                {/* Financial Trends Chart */}
                <Grid item xs={12}>
                    <FinancialTrendsChart />
                </Grid>

                {/* Outstanding Folio Dues Table */}
                <Grid item xs={12}>
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 3, 
                            borderRadius: '12px', 
                            border: '1px solid #eaecf0', 
                            bgcolor: '#ffffff',
                            boxShadow: 'none'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ p: 1, borderRadius: '8px', bgcolor: 'rgba(234, 88, 12, 0.1)', color: '#ea580c', display: 'flex' }}>
                                    <ReceiptLongIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" fontWeight="700" sx={{ color: '#0f172a', fontSize: '16px' }}>
                                            Outstanding Folio Receivables
                                        </Typography>
                                        <Chip 
                                            label={`${outstandingDues.length} Folios`} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: '#fff7ed', 
                                                color: '#c2410c', 
                                                fontWeight: 700, 
                                                fontSize: '11px',
                                                border: '1px solid #ffedd5' 
                                            }} 
                                        />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        Unsettled balances and aging receivables from active guest folios
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <TextField
                                    size="small"
                                    placeholder="Search folio, reservation, or guest ID..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setPage(0);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ 
                                        ...standardSearchInputSx,
                                        width: { xs: '100%', sm: 260, md: 300 }
                                    }}
                                />
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        Total Outstanding
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#ea580c' }}>
                                        {formatCurrency(totalOutstandingDues, currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Folio Reference</TableCell>
                                        <TableCell>Reservation Ref</TableCell>
                                        <TableCell>Guest Ref</TableCell>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell>Status / Aging</TableCell>
                                        <TableCell align="right">Outstanding Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredDues.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                                                {loading ? 'Loading outstanding folios...' : 'No outstanding dues found.'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredDues
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((item, idx) => {
                                                const dDate = item.dueDate ? new Date(item.dueDate) : null;
                                                const isOverdue = dDate && dDate < new Date();
                                                const daysOverdue = dDate ? Math.max(0, Math.floor((new Date() - dDate) / (1000 * 60 * 60 * 24))) : 0;
                                                
                                                return (
                                                    <TableRow key={item.folioId || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', fontSize: '12px' }}>
                                                            <Tooltip title={item.folioId || ''}>
                                                                <span>FOL-{item.folioId ? item.folioId.substring(0, 8) : 'N/A'}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#475569', fontSize: '12px', fontFamily: 'monospace' }}>
                                                            <Tooltip title={item.reservationId || ''}>
                                                                <span>RES-{item.reservationId ? item.reservationId.substring(0, 8) : '—'}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#475569', fontSize: '12px', fontFamily: 'monospace' }}>
                                                            <Tooltip title={item.guestId || ''}>
                                                                <span>GST-{item.guestId ? item.guestId.substring(0, 8) : '—'}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#334155', fontSize: '12px' }}>
                                                            {dDate ? dDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {isOverdue ? (
                                                                <Chip 
                                                                    label={daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Overdue'}
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: '#fef2f2', 
                                                                        color: '#b91c1c', 
                                                                        fontWeight: 700, 
                                                                        fontSize: '11px',
                                                                        height: 22,
                                                                        border: '1px solid #fecaca' 
                                                                    }} 
                                                                />
                                                            ) : (
                                                                <Chip 
                                                                    label="Due soon" 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: '#f0fdf4', 
                                                                        color: '#15803d', 
                                                                        fontWeight: 600, 
                                                                        fontSize: '11px',
                                                                        height: 22,
                                                                        border: '1px solid #bbf7d0' 
                                                                    }} 
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#c2410c', fontSize: '13px' }}>
                                                            {formatCurrency(item.amount, currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredDues.length === 0 ? 0 : filteredDues.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
